import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Search, ArrowRight, Zap } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { paletteNavigationEntries, quickActions, adminPaletteEntry } from './navRegistry';

/**
 * CommandPalette - ⌘K command palette (cmdk, DESIGN-SPEC.md §3/§6, decision CC2).
 *
 * Absorbs the old AppSidebar "Quick Actions" group + provides jump-to-any-
 * screen navigation, seeded from the shared navRegistry (built FROM
 * AppSidebar's registry — see navRegistry.js header). Global ⌘K/Ctrl+K
 * listener lives in AppLayout; this component only renders once open.
 *
 * - Esc closes (native cmdk/Radix Dialog behavior).
 * - `prefers-reduced-motion` disables the entrance animation (§6/§7).
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(action: string) => void} props.onAction - dispatched via useV2Actions().handleAction
 * @param {boolean} props.isAdmin - include the admin-only nav entry
 * @param {number} props.insightsUnreadCount
 * @param {number} props.eventsUnreadCount
 */
export function CommandPalette({
  open,
  onOpenChange,
  onAction,
  isAdmin = false,
  insightsUnreadCount = 0,
  eventsUnreadCount = 0,
}) {
  const navigate = useNavigate();
  const prefersReducedMotion = usePrefersReducedMotion();

  const navigationEntries = React.useMemo(() => {
    return isAdmin ? [...paletteNavigationEntries, adminPaletteEntry] : paletteNavigationEntries;
  }, [isAdmin]);

  const runNavigate = React.useCallback(
    (url) => {
      onOpenChange(false);
      navigate(url);
    },
    [navigate, onOpenChange]
  );

  const runAction = React.useCallback(
    (action) => {
      onOpenChange(false);
      onAction?.(action);
    },
    [onAction, onOpenChange]
  );

  const unreadCountFor = (key) => {
    if (key === 'insights') return insightsUnreadCount;
    if (key === 'events') return eventsUnreadCount;
    return 0;
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      shouldFilter
      loop
      overlayClassName="fixed inset-0 z-50 bg-black/60"
      contentClassName={cn(
        'fixed left-1/2 top-[18vh] z-50 w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2 overflow-hidden rounded-lg border border-border bg-popover shadow-2xl focus:outline-none',
        // §6 is categorical: "No transform/scale animations" for ALL users,
        // not just reduced-motion — a plain opacity fade is the only
        // permitted entrance animation (issue #232 review fix, Design #3).
        // `-translate-x-1/2` above is static positioning, not an animation.
        !prefersReducedMotion &&
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-150'
      )}
      data-testid="command-palette"
    >
      <div className="flex items-center gap-2 border-b border-border px-3.5">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <Command.Input
          autoFocus
          placeholder="Command… buy, sell, jump to"
          aria-label="Search commands and pages"
          className="h-12 w-full bg-transparent font-sans text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        <kbd className="hidden shrink-0 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
          Esc
        </kbd>
      </div>

      <Command.List className="max-h-[360px] overflow-y-auto p-2">
        <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
          No results found.
        </Command.Empty>

        <Command.Group
          heading="Quick actions"
          className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground [&_[cmdk-group-heading]]:px-1 [&_[cmdk-group-heading]]:pb-1"
        >
          {quickActions.map((item) => (
            <Command.Item
              key={item.id}
              value={`${item.label} ${item.keywords || ''}`}
              onSelect={() => runAction(item.action)}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent-tint data-[selected=true]:text-[var(--accent-text,_#8fc4f7)]"
            >
              <Zap className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {item.label}
            </Command.Item>
          ))}
        </Command.Group>

        <Command.Group
          heading="Jump to"
          className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground [&_[cmdk-group-heading]]:px-1 [&_[cmdk-group-heading]]:pb-1"
        >
          {navigationEntries.map((item) => {
            const count = unreadCountFor(item.unread);
            return (
              <Command.Item
                key={item.id}
                value={`${item.label} ${item.keywords || ''}`}
                onSelect={() => runNavigate(item.url)}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground data-[selected=true]:bg-accent-tint data-[selected=true]:text-[var(--accent-text,_#8fc4f7)]"
              >
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="flex-1">{item.label}</span>
                {count > 0 && (
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--accent,_#3b9eff)]"
                    aria-label={`${count} unread`}
                  />
                )}
              </Command.Item>
            );
          })}
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
}

export default CommandPalette;
